// "Counts as a work day" threshold (admin). Hours and days are two independent ledgers: extra
// hours can never manufacture a day that didn't happen. This sets the minimum a person must
// actually work for the day to count. It is VERSIONED — a change applies from its date forward
// and never re-counts days already gone.
import React, { useEffect, useState } from 'react';
import { authFetch, API } from './api';

interface Row {
  id: number; effective_from: string; threshold_min: number;
  note: string | null; created_at: string; created_by: string;
}

const inp = 'bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1 focus:outline-none focus:border-blue-500';
const hm = (m: number) => `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}`;

export default function DayThresholdPanel() {
  const [history, setHistory] = useState<Row[]>([]);
  const [current, setCurrent] = useState<number>(240);
  const [hours, setHours] = useState('4');
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const r = await authFetch(`${API}/statistics/work-day-threshold`);
    if (!r.ok) { setErr('Could not load the threshold'); return; }
    const d = await r.json();
    setHistory(d.history || []);
    setCurrent(d.current_min ?? 240);
    setHours(String((d.current_min ?? 240) / 60));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const mins = Math.round(parseFloat(hours) * 60);
    if (!isFinite(mins) || mins < 0 || mins > 1440) { setErr('Enter between 0 and 24 hours'); return; }
    setBusy(true); setErr(null);
    const r = await authFetch(`${API}/statistics/work-day-threshold`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ threshold_min: mins, effective_from: from, note: note || null }),
    });
    setBusy(false);
    if (!r.ok) {
      let m = `${r.status}`;
      try { m = (await r.json()).detail || m; } catch { /* keep status */ }
      setErr(m); return;
    }
    setNote('');
    load();
  };

  return (
    <div>
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">
        Counts as a work day
      </div>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-4 py-3">
        <p className="text-xs text-gray-500 mb-3">
          A day counts only if the person actually worked at least this much — after any hours used to
          work back an earlier shortfall. That is what stops eight days of overtime from covering up one
          missed day. Currently <span className="text-gray-200 font-medium">{hm(current)}</span>.
          <span className="block mt-1 text-gray-600">
            Applies to regular workers only. People on monthly-hours or on-call contracts are tracked by
            hours, so this never applies to them.
          </span>
        </p>

        {err && (
          <div className="mb-2 bg-red-950/40 border border-red-900/60 text-red-300 text-xs rounded-lg px-3 py-2">{err}</div>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-500">
            Minimum hours
            <input type="number" min={0} max={24} step={0.5} className={`${inp} ml-2 w-20`}
              value={hours} onChange={e => setHours(e.target.value)} />
          </label>
          <label className="text-xs text-gray-500">
            From
            <input type="date" className={`${inp} ml-2`} value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <input className={`${inp} flex-1 min-w-[160px]`} value={note}
            onChange={e => setNote(e.target.value)} placeholder="Why (optional)" />
          <button onClick={save} disabled={busy}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs rounded-lg font-medium">
            {busy ? 'Saving…' : 'Apply from date'}
          </button>
        </div>

        <p className="text-[11px] text-gray-600 mt-2">
          Changing this never rewrites the past — days before the chosen date keep the value that was in
          force when they happened.
        </p>

        {history.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-800">
            <button onClick={() => setOpen(o => !o)} className="text-[11px] text-gray-500 hover:text-gray-300">
              {open ? '▾' : '▸'} History ({history.length})
            </button>
            {open && (
              <div className="mt-2 space-y-1">
                {history.map(h => (
                  <div key={h.id} className="flex flex-wrap gap-x-3 text-[11px] text-gray-500">
                    <span className="text-gray-300 min-w-[92px]">{h.effective_from}</span>
                    <span className="text-gray-200 min-w-[52px]">{hm(h.threshold_min)}</span>
                    <span>set by {h.created_by}</span>
                    {h.note && <span className="text-gray-600 italic">— {h.note}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
